import type { LinkedInCompanyReportData } from "@/components/reports/linkedin-company-report";
import { lookbookReportMeta } from "@/lib/lookbook-reports";

/**
 * Finding the content inside a shared report row, and deciding what a reader
 * should never see.
 *
 * Split out from lookbook-reports.tsx so the shape-matching is testable without
 * a DOM: the shapes come from storage, they differ per report, and getting one
 * wrong shows a visitor an apology where the report should be.
 */

/**
 * Where each report's content sits inside the item the server returns.
 *
 * The item is the storage row, so the payload is under the *stored field* name,
 * which is not the report key: `tearsheets` arrives as `{ tearsheet: {...} }`,
 * `startupscores` as `{ startupscore: {...} }`. Keep this in step with
 * AI_GENERATION_FIELD and fetchFromCollection in
 * startupfuel/controllers/applications/report_fetchers.ts — that file decides
 * the shape and this one only has to find it.
 */
export const REPORT_PAYLOAD_FIELD: Record<string, string> = {
  startupscores: "startupscore",
  summaries: "summary",
  tearsheets: "tearsheet",
  market_intel: "market_intel",
  dealMemo: "dealMemo",
  valuations: "valuations",
  metrics: "metrics",
  website_summary_v2: "website_summary",
  websiteInfo: "websiteInfo",
  market_research: "market_research_final",
  linkedInInfo: "data",
  insights: "insights",
  // The fetcher aliases the stored `gap_analysis` array to `questions`, the
  // name get_reports and the internal report both use.
  gap_analysis: "questions",
  datasufficiencyscores: "datasufficiencyscore",
  datasufficiencyscores_v2: "datasufficiencyscore_v2",
};

/**
 * Bookkeeping the storage rows carry that a reader has no use for — ids, the
 * foreign keys, generation cost, and `file_url`/`sources`, which are S3 links
 * this page must never surface. The server strips those too; this is the second
 * lock, because the cost of a miss here is a public link to a private document.
 */
export const HIDDEN_KEYS = new Set([
  "_id",
  "id",
  "__v",
  "company_id",
  "pertains_to_opportunity",
  "opportunity_application_id",
  "created_on",
  "updated_on",
  "timestamp",
  "credits_used",
  "file_url",
  "file_size",
  "sources",
  "model",
  // Per-engine scores behind an insight. Which models a fund runs, and how they
  // disagreed, is our plumbing — the response is the part a reader came for.
  "engine_results",
  "status",
  "version_metadata",
  "summary_timestamp",
  "DateCreated",
]);

/** The report's content, by its stored field name, with sensible fallbacks. */
export function extractPayload(
  reportKey: string,
  item: Record<string, unknown>
): unknown {
  const field = REPORT_PAYLOAD_FIELD[reportKey];
  const direct = field ? item[field] : undefined;
  if (!isEmptyValue(direct)) return direct;

  // market_research keeps the merged answer in market_research_final and the
  // per-model runs in market_research; publish the merged one, or the first run
  // if a merge was never written.
  if (reportKey === "market_research" && Array.isArray(item.market_research)) {
    return item.market_research[0];
  }

  // A key we have no mapping for: fall back to the report key itself, which is
  // how the collection-backed reports are named.
  return item[reportKey];
}

/**
 * Prefers the richer storage when two keys share a title.
 *
 * `tearsheets` (a flat AiGeneration field) and `tearsheets_v2` (the
 * customize-UI document) are one report to a reader, and a deal that has been
 * through the newer flow has both. The v2 document is the one the fund has
 * actually laid out, so it wins — order in `report_keys` follows
 * SHAREABLE_REPORT_KEYS, which happens to list the legacy key first.
 *
 * Data sufficiency is the same story: v2 scores per question with reasoning,
 * v1 is six category scores, and a deal that has both should show the one that
 * says more.
 */
const PREFERRED_KEYS = new Set(["tearsheets_v2", "datasufficiencyscores_v2"]);

export function dedupeByTitle(keys: string[]): string[] {
  const byTitle = new Map<string, string>();
  for (const key of keys) {
    const title = lookbookReportMeta(key).title;
    if (!byTitle.has(title) || PREFERRED_KEYS.has(key)) {
      byTitle.set(title, key);
    }
  }
  // Map preserves the position of the first key seen for a title, so reading
  // order still follows the stored order rather than the preference.
  return [...byTitle.values()];
}

/**
 * Tearsheets: the customize-UI payload, from the newest version that has one.
 *
 * Two shapes live under `ui_data` and this deliberately returns either: a
 * standalone HTML document from the current worker, or the GenUI schema tree
 * everything older carries. Telling them apart is htmlPayloadOf's job.
 */
export function extractUiData(
  item: Record<string, unknown>
): unknown | null {
  const data = item.data;
  if (!Array.isArray(data)) return null;
  for (let i = data.length - 1; i >= 0; i--) {
    const uiData = (data[i] as { ui_data?: unknown })?.ui_data;
    if (!isEmptyValue(uiData)) return uiData;
  }
  return null;
}

/**
 * True when a LinkedIn scrape has anything the shared renderer will show.
 *
 * That renderer draws a curated set of fields, so a row can be non-empty in the
 * `isEmptyValue` sense — an `input` url, a company id — and still render as a
 * blank card. Checked here so the page can fall through to "This report is
 * empty" instead.
 */
export function hasLinkedInReportContent(
  data: LinkedInCompanyReportData | null | undefined
): boolean {
  if (!data) return false;
  return Boolean(
    data.name ||
      data.about ||
      data.slogan ||
      data.industries ||
      data.specialties ||
      data.headquarters ||
      data.website ||
      data.organization_type ||
      data.company_size ||
      data.founded ||
      (data.followers ?? 0) > 0 ||
      (data.employees_in_linkedin ?? 0) > 0 ||
      data.funding ||
      (data.locations ?? []).length > 0 ||
      (data.employees ?? []).length > 0 ||
      (data.similar ?? []).length > 0 ||
      (data.updates ?? []).length > 0
  );
}

export interface MetricItem {
  title?: string;
  category?: string;
  sub_category?: string;
  data?: Array<{ title?: string; value?: unknown }>;
}

export function extractMetrics(item: Record<string, unknown>): MetricItem[] {
  return Array.isArray(item.metrics) ? (item.metrics as MetricItem[]) : [];
}

/** A metric's headline figure — the first data point that isn't an assumption. */
export function metricValue(metric: MetricItem): string {
  const points = Array.isArray(metric?.data) ? metric.data : [];
  const primary =
    points.find((p) => p?.title && p.title.toLowerCase() !== "assumption") ??
    points[0];
  return primary ? formatScalar(primary.value) : "—";
}

const IMAGE_EXTENSIONS = /\.(png|jpe?g|gif|webp|avif|svg)$/i;

/**
 * Hosts whose urls are images even though the path carries no extension.
 *
 * LinkedIn's media CDN is the reason this exists: a scraped employee's `img` is
 * `https://media.licdn.com/dms/image/v2/D4E03.../profile-displayphoto...` with
 * the format in a query string, so an extension test alone reads it as prose
 * and the public report prints the raw url where the face should be.
 *
 * Matched on the registrable suffix, so `media.licdn.com`,
 * `media-exp1.licdn.com` and `static.licdn.com` are all covered, and a host
 * that merely ends in the same letters (`evillicdn.com`) is not.
 */
const IMAGE_HOSTS = ["licdn.com", "media.licdn.com"];

/**
 * The url of an image a report means to *show*, or null if the string is prose,
 * a link, or anything else.
 *
 * Only http(s). A `data:` url would render, but nothing upstream produces one
 * and accepting it would mean this page paints bytes chosen by whoever wrote
 * the report row.
 */
export function imageUrl(value: unknown): string | null {
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  if (trimmed.length === 0) return null;

  let url: URL;
  try {
    url = new URL(trimmed);
  } catch {
    return null;
  }
  if (url.protocol !== "https:" && url.protocol !== "http:") return null;

  const host = url.hostname.toLowerCase();
  const hosted = IMAGE_HOSTS.some(
    (candidate) => host === candidate || host.endsWith(`.${candidate}`)
  );

  return hosted || IMAGE_EXTENSIONS.test(url.pathname) ? trimmed : null;
}

export const isScalar = (value: unknown): boolean =>
  typeof value === "string" ||
  typeof value === "number" ||
  typeof value === "boolean";

/** Empty in the sense that rendering it would produce a heading over nothing. */
export function isEmptyValue(value: unknown): boolean {
  if (value == null) return true;
  if (typeof value === "string") return value.trim().length === 0;
  if (Array.isArray(value)) return value.every(isEmptyValue);
  if (typeof value === "object") {
    return Object.entries(value as Record<string, unknown>).every(
      ([key, entry]) => HIDDEN_KEYS.has(key) || isEmptyValue(entry)
    );
  }
  return false;
}

export function formatScalar(value: unknown): string {
  if (value == null) return "—";
  // Pinned to en-US rather than the runtime default: a bare toLocaleString()
  // renders 1200000 as "12,00,000" under an Indian locale and "1.200.000"
  // under a German one, so the same lookbook read differently depending on
  // the viewer's machine — and the test for it passed or failed depending on
  // the developer's.
  if (typeof value === "number") return value.toLocaleString("en-US");
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
}

/**
 * A storage key as a heading: `Market_Summary`, `overallScore` and
 * `market_research_final` all have to read as English. Acronyms the models emit
 * in caps (TAM, SAM, SOM) are left alone.
 */
export function humanize(key: string): string {
  const words = key
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/[_-]+/g, " ")
    .trim()
    .split(/\s+/)
    .filter(Boolean);

  if (words.length === 0) return key;

  return words
    .map((word) =>
      word === word.toUpperCase() ? word : word[0].toUpperCase() + word.slice(1)
    )
    .join(" ");
}
